import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
} from 'react-icons/fa';
import PageSizeSelector from '../../DataTable/PageSizeSelector';

interface PaginationProps {
  paginator: {
    number: number;
    totalPages: number;
    totalElements: number;
    first: boolean;
    last: boolean;
  };
  data: any[];
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
}

const UniversalTablePagination: React.FC<PaginationProps> = ({
  paginator,
  data,
  pageSize,
  onPageSizeChange,
  onPageChange,
}) => {
  const currentPage = paginator?.number ?? 0;
  const totalPages = paginator?.totalPages ?? 0;
  const first = paginator?.first ?? true;
  const last = paginator?.last ?? false;

  const visiblePageCount = 5;
  const startPage = Math.max(0, currentPage - Math.floor(visiblePageCount / 2));
  const endPage = Math.min(startPage + visiblePageCount - 1, totalPages - 1);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div
      className="
      flex
      flex-col
      gap-4

      border-t
      border-stroke

      px-3
      py-4

      dark:border-strokedark

      sm:px-4

      lg:flex-row
      lg:items-center
      lg:justify-between
    "
    >
      {/* ===================================================== */}
      {/* 🔹 Info */}
      {/* ===================================================== */}

      <p
        className="
        text-center

        text-xs
        font-medium

        text-body

        dark:text-bodydark1

        sm:text-sm
      "
      >
        Mostrando página {currentPage + 1} de {totalPages}
      </p>

      {/* ===================================================== */}
      {/* 🔹 Pagination */}
      {/* ===================================================== */}

      <nav
        className="
        no-scrollbar
        flex

        justify-center

        overflow-x-auto
      "
      >
        <ul
          className="
          flex
          min-w-max
          items-center
        "
        >
          {/* ===================================================== */}
          {/* First */}
          {/* ===================================================== */}

          <li>
            <button
              onClick={() => handlePageChange(0)}
              disabled={first}
              className={`
              flex
              h-9
              w-9

              items-center
              justify-center

              rounded-l-lg

              border
              border-stroke

              bg-whiten

              text-body

              transition-all
              duration-200

              focus-visible:z-10
              focus-visible:border-primary

              focus-visible:ring-2
              focus-visible:ring-primary/20
              dark:border-strokedark
              dark:bg-boxdark

              dark:text-bodydark1
              sm:h-10
              sm:w-10

              ${
                first
                  ? `
                    cursor-not-allowed

                    bg-gray

                    text-bodydark2

                    opacity-70

                    dark:bg-boxdark-2
                  `
                  : `
                    hover:bg-gray
                    hover:text-primary

                    dark:hover:bg-boxdark-2
                  `
              }
            `}
            >
              <FaAngleDoubleLeft />
            </button>
          </li>

          {/* ===================================================== */}
          {/* Previous */}
          {/* ===================================================== */}

          <li>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={first}
              className={`
              flex
              h-9
              w-9

              items-center
              justify-center

              border
              border-stroke

              bg-whiten

              text-body

              transition-all
              duration-200

              focus-visible:z-10
              focus-visible:border-primary

              focus-visible:ring-2
              focus-visible:ring-primary/20
              dark:border-strokedark
              dark:bg-boxdark

              dark:text-bodydark1
              sm:h-10
              sm:w-10

              ${
                first
                  ? `
                    cursor-not-allowed

                    bg-gray

                    text-bodydark2

                    opacity-70

                    dark:bg-boxdark-2
                  `
                  : `
                    hover:bg-gray
                    hover:text-primary

                    dark:hover:bg-boxdark-2
                  `
              }
            `}
            >
              <FaAngleLeft />
            </button>
          </li>

          {/* ===================================================== */}
          {/* Pages */}
          {/* ===================================================== */}

          {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
            <li key={startPage + i}>
              <button
                onClick={() => handlePageChange(startPage + i)}
                className={`
                  flex
                  min-w-[36px]
                  items-center

                  justify-center

                  border
                  border-stroke

                  px-3
                  py-2

                  text-sm
                  font-medium

                  transition-all
                  duration-200

                  focus-visible:z-10
                  focus-visible:border-primary

                  focus-visible:ring-2
                  focus-visible:ring-primary/20
                  dark:border-strokedark
                  sm:min-w-[40px]

                  sm:px-4

                  ${
                    currentPage === startPage + i
                      ? `
                        border-primary

                        bg-primary

                        text-white

                        shadow-1
                      `
                      : `
                        bg-whiten

                        text-body

                        hover:bg-gray
                        hover:text-primary

                        dark:bg-boxdark
                        dark:text-bodydark1

                        dark:hover:bg-boxdark-2
                      `
                  }
                `}
              >
                {startPage + i + 1}
              </button>
            </li>
          ))}

          {/* ===================================================== */}
          {/* Next */}
          {/* ===================================================== */}

          <li>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={last}
              className={`
              flex
              h-9
              w-9

              items-center
              justify-center

              border
              border-stroke

              bg-whiten

              text-body

              transition-all
              duration-200

              focus-visible:z-10
              focus-visible:border-primary

              focus-visible:ring-2
              focus-visible:ring-primary/20
              dark:border-strokedark
              dark:bg-boxdark

              dark:text-bodydark1
              sm:h-10
              sm:w-10

              ${
                last
                  ? `
                    cursor-not-allowed

                    bg-gray

                    text-bodydark2

                    opacity-70

                    dark:bg-boxdark-2
                  `
                  : `
                    hover:bg-gray
                    hover:text-primary

                    dark:hover:bg-boxdark-2
                  `
              }
            `}
            >
              <FaAngleRight />
            </button>
          </li>

          {/* ===================================================== */}
          {/* Last */}
          {/* ===================================================== */}

          <li>
            <button
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={last}
              className={`
              flex
              h-9
              w-9

              items-center
              justify-center

              rounded-r-lg

              border
              border-stroke

              bg-whiten

              text-body

              transition-all
              duration-200

              focus-visible:z-10
              focus-visible:border-primary

              focus-visible:ring-2
              focus-visible:ring-primary/20
              dark:border-strokedark
              dark:bg-boxdark

              dark:text-bodydark1
              sm:h-10
              sm:w-10

              ${
                last
                  ? `
                    cursor-not-allowed

                    bg-gray

                    text-bodydark2

                    opacity-70

                    dark:bg-boxdark-2
                  `
                  : `
                    hover:bg-gray
                    hover:text-primary

                    dark:hover:bg-boxdark-2
                  `
              }
            `}
            >
              <FaAngleDoubleRight />
            </button>
          </li>
        </ul>
      </nav>

      {/* ===================================================== */}
      {/* 🔹 Page Size */}
      {/* ===================================================== */}

      <div
        className="
        flex
        justify-center

        lg:justify-end
      "
      >
        <PageSizeSelector
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          disabled={!data || data.length === 0}
        />
      </div>
    </div>
  );
};

export default UniversalTablePagination;
